import React, { useState, Component } from 'react';
/**
 * InvisibleRuleGame
 * A fully self-contained, inline-styled psychology micro-game.
 * Design system is intentional and brand-consistent:
 *   charcoal #1A1814, cream #F4F0E8, mauve #7F77DD
 * Fonts: Playfair Display (headings), DM Sans (body)
 * Dependencies: React useState only. No external libs, no API calls.
 */
const CHARCOAL = '#1A1814';
const CREAM = '#F4F0E8';
const MAUVE = '#7F77DD';
const CHARCOAL_SOFT = 'rgba(26,24,20,0.62)';
const CHARCOAL_LINE = 'rgba(26,24,20,0.14)';
const serif = "'Playfair Display', Georgia, serif";
const sans = "'DM Sans', system-ui, sans-serif";
type Path = 'architect' | 'improviser';
interface Question {
  prompt: string;
  a: {
    label: string;
    path: Path;
  };
  b: {
    label: string;
    path: Path;
  };
}
// 3 rotating question sets — auto-rotates on Play Again
const QUESTION_SETS: Question[][] = [[{
  prompt: 'A blank calendar week appears. Your first instinct?',
  a: {
    label: 'Block it into themed days before lunch',
    path: 'architect'
  },
  b: {
    label: 'Leave it open and see what the week asks for',
    path: 'improviser'
  }
}, {
  prompt: 'You join a new team. The unspoken rules feel unclear.',
  a: {
    label: 'Quietly map who holds what power',
    path: 'architect'
  },
  b: {
    label: 'Start talking to people and feel it out',
    path: 'improviser'
  }
}, {
  prompt: 'A plan you made collapses an hour before.',
  a: {
    label: 'Rebuild a tighter version on the spot',
    path: 'architect'
  },
  b: {
    label: 'Shrug, improvise, maybe it is better this way',
    path: 'improviser'
  }
}], [{
  prompt: 'You are handed a project with zero brief.',
  a: {
    label: 'Write the brief nobody gave you',
    path: 'architect'
  },
  b: {
    label: 'Make three things and show them',
    path: 'improviser'
  }
}, {
  prompt: 'A friend asks where to eat. You...',
  a: {
    label: 'Already have a ranked shortlist',
    path: 'architect'
  },
  b: {
    label: 'Walk until something looks right',
    path: 'improviser'
  }
}, {
  prompt: 'The group chat goes silent on a decision.',
  a: {
    label: 'Send a structured poll with options',
    path: 'architect'
  },
  b: {
    label: 'Just pick something and announce it',
    path: 'improviser'
  }
}], [{
  prompt: 'You are early to a meeting that has not started.',
  a: {
    label: 'Re-read the agenda and prep openers',
    path: 'architect'
  },
  b: {
    label: 'Chat with whoever is in the room',
    path: 'improviser'
  }
}, {
  prompt: 'A tool you rely on changes overnight.',
  a: {
    label: 'Learn the new system end to end',
    path: 'architect'
  },
  b: {
    label: 'Poke at it until it works',
    path: 'improviser'
  }
}, {
  prompt: 'Someone breaks a rule that was never written down.',
  a: {
    label: 'You notice instantly — you wrote it in your head',
    path: 'architect'
  },
  b: {
    label: 'You did not know it existed',
    path: 'improviser'
  }
}]];
interface Result {
  term: string;
  definition: string;
  blurb: string;
  personal: string;
  professional: string;
  famous: {
    name: string;
    note: string;
  }[];
}
const RESULTS: Record<Path, Result> = {
  architect: {
    term: 'The Architect',
    definition: 'Structure-seeking. You reduce uncertainty by building invisible rules — frameworks, rankings, and systems — before the world forces a choice on you.',
    blurb: 'You feel calm when the path is mapped. Ambiguity is not your enemy; an unexamined ambiguity is.',
    personal: 'In your personal life, this shows up as plans that quietly carry the people around you. The risk: you mistake your map for the territory and feel friction when others wander off it.',
    professional: 'At work, you are the person who writes the doc nobody asked for — and then everyone relies on. Your edge is foresight. Your blind spot is over-engineering the simple.',
    famous: [{
      name: 'Tim Cook',
      note: 'Operations as art'
    }, {
      name: 'Marie Kondo',
      note: 'A system for everything'
    }, {
      name: 'Hayao Miyazaki',
      note: 'Worlds built rule-first'
    }]
  },
  improviser: {
    term: 'The Improviser',
    definition: 'Emergence-seeking. You trust that the right structure reveals itself through motion. You make the rule by breaking into the moment.',
    blurb: 'You feel alive when the next step is unknown. A finished plan can feel like a cage you did not agree to.',
    personal: 'In your personal life, you are the one who turns a dull evening into a story. The risk: the people who love structure can read your spontaneity as unreliability.',
    professional: 'At work, you unstick rooms that planning froze. Your edge is adaptability. Your blind spot is the slow, unglamorous follow-through that compounds.',
    famous: [{
      name: 'Anthony Bourdain',
      note: 'Wander, then understand'
    }, {
      name: 'Miles Davis',
      note: 'The note you do not play'
    }, {
      name: 'Jacinda Ardern',
      note: 'Read the room, lead it'
    }]
  }
};
// ---- Hand-drawn SVG sketches ----------------------------------------------
function SketchKnot() {
  return <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true" data-id="element-78">
      <path d="M14 40c8-22 28-22 36 0M12 40c10 10 30 10 40 0M20 30c4-6 20-6 24 0" stroke={MAUVE} strokeWidth="2.4" strokeLinecap="round" fill="none" data-id="element-79" />
      <circle cx="32" cy="22" r="3" fill={MAUVE} data-id="element-80" />
    </svg>;
}
function SketchGrid() {
  return <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true" data-id="element-81">
      <path d="M8 18c14-3 26-3 40 0M8 30c14-3 26-3 40 1M9 42c13-2 27-2 39 1M18 9c-2 13-2 27 0 39M30 8c-2 14-2 26 1 40M42 9c-2 13-2 27 0 38" stroke={CREAM} strokeWidth="2" strokeLinecap="round" opacity="0.85" data-id="element-82" />
    </svg>;
}
function SketchPath() {
  return <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true" data-id="element-83">
      <path d="M12 46c2-12 10-14 16-10s12 2 16-8" stroke={CREAM} strokeWidth="2.2" strokeLinecap="round" strokeDasharray="1 6" data-id="element-84" />
      <path d="M40 18l6 2-2 6" stroke={CREAM} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-85" />
    </svg>;
}
// ---- Component -------------------------------------------------------------
type Step = 'intro' | 'quiz' | 'result';
export function InvisibleRuleGame({
  onClose
}: {
  onClose?: () => void;
}) {
  const [step, setStep] = useState<Step>('intro');
  const [setIndex, setSetIndex] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [scores, setScores] = useState<Record<Path, number>>({
    architect: 0,
    improviser: 0
  });
  const [disagree, setDisagree] = useState('');
  const [disagreeSent, setDisagreeSent] = useState(false);
  const questions = QUESTION_SETS[setIndex];
  const result: Result | null = step === 'result' ? RESULTS[scores.architect >= scores.improviser ? 'architect' : 'improviser'] : null;
  function choose(path: Path) {
    const next = {
      ...scores,
      [path]: scores[path] + 1
    };
    setScores(next);
    if (qIndex + 1 < questions.length) {
      setQIndex(qIndex + 1);
    } else {
      setStep('result');
    }
  }
  function playAgain() {
    setSetIndex((setIndex + 1) % QUESTION_SETS.length); // rotate question set
    setQIndex(0);
    setScores({
      architect: 0,
      improviser: 0
    });
    setDisagree('');
    setDisagreeSent(false);
    setStep('intro');
  }
  function submitDisagree(e: React.FormEvent) {
    e.preventDefault();
    if (!disagree.trim()) return;
    // Optional: wire this to Formspree / Airtable here.
    setDisagreeSent(true);
  }
  // Shareable URL for the result. The query param lets the page render a
  // result-specific Open Graph image (og:image) so LinkedIn shows the right
  // card preview when it scrapes this URL.
  const SITE = 'https://underlinedbydania.com';
  function shareUrl(r: Result) {
    const path = RESULTS.architect === r ? 'architect' : 'improviser';
    return `${SITE}/game?result=${path}`;
  }
  // Opens LinkedIn's share dialog as a centered popup window. LinkedIn only
  // accepts a `url` — it pulls the image/preview from that page's og:image —
  // so we also copy the post caption to the clipboard for easy pasting.
  function postToLinkedIn(r: Result) {
    const url = shareUrl(r);
    const caption = `My invisible rule: ${r.term}. ${r.definition} I took The Invisible Rule by Underlined by Dania — what's yours? ${url}`;
    try {
      navigator.clipboard?.writeText(caption);
    } catch {
      /* clipboard may be unavailable; the popup still opens */
    }
    const share = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    const w = 600;
    const h = 640;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(share, 'linkedin-share', `popup=yes,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
  }
  const card: React.CSSProperties = {
    fontFamily: sans,
    width: '100%',
    maxWidth: step === 'result' ? 720 : 520,
    background: CREAM,
    color: CHARCOAL,
    borderRadius: 24,
    padding: 36,
    boxShadow: '0 30px 80px rgba(26,24,20,0.35)',
    position: 'relative',
    overflow: 'hidden'
  };
  const pill: React.CSSProperties = {
    display: 'inline-block',
    fontFamily: sans,
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: MAUVE,
    fontWeight: 700,
    marginBottom: 14
  };
  const choiceBtn: React.CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    fontFamily: sans,
    fontSize: 16,
    fontWeight: 500,
    color: CHARCOAL,
    background: '#fff',
    border: `1px solid ${CHARCOAL_LINE}`,
    borderRadius: 14,
    padding: '16px 18px',
    cursor: 'pointer',
    marginBottom: 12,
    lineHeight: 1.4,
    transition: 'transform 0.15s ease, border-color 0.15s ease'
  };
  const primaryBtn: React.CSSProperties = {
    fontFamily: sans,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: CREAM,
    background: CHARCOAL,
    border: 'none',
    borderRadius: 999,
    padding: '14px 26px',
    cursor: 'pointer'
  };
  const ghostBtn: React.CSSProperties = {
    ...primaryBtn,
    color: CHARCOAL,
    background: 'transparent',
    border: `1.5px solid ${CHARCOAL}`
  };
  return <div style={card} role="dialog" aria-label="The Invisible Rule game" data-id="element-86">
      {/* Close */}
      {onClose && <button onClick={onClose} aria-label="Close game" style={{
      position: 'absolute',
      top: 16,
      right: 16,
      width: 34,
      height: 34,
      borderRadius: 999,
      border: `1px solid ${CHARCOAL_LINE}`,
      background: '#fff',
      color: CHARCOAL,
      fontSize: 18,
      lineHeight: 1,
      cursor: 'pointer',
      zIndex: 2
    }} data-id="element-87">
          ×
        </button>}

      {/* INTRO */}
      {step === 'intro' && <div data-id="element-88">
          <span style={pill} data-id="element-89">A 60-second experiment</span>
          <div style={{
        marginBottom: 18
      }} data-id="element-90">
            <SketchKnot data-id="element-91" />
          </div>
          <h2 style={{
        fontFamily: serif,
        fontSize: 32,
        lineHeight: 1.1,
        margin: '0 0 14px'
      }} data-id="element-92">
            The Invisible Rule
          </h2>
          <p style={{
        fontSize: 16,
        lineHeight: 1.6,
        color: CHARCOAL_SOFT,
        margin: '0 0 26px'
      }} data-id="element-93">
            Everyone carries a private rulebook for how the world should work —
            most of us never read it out loud. Three questions. No wrong
            answers. Let's find a page of yours.
          </p>
          <button style={primaryBtn} onClick={() => {
        setStep('quiz');
      }} data-id="element-94">
            Begin →
          </button>
        </div>}

      {/* QUIZ */}
      {step === 'quiz' && <div data-id="element-95">
          <span style={pill} data-id="element-96">
            Question {qIndex + 1} of {questions.length}
          </span>
          {/* progress */}
          <div style={{
        display: 'flex',
        gap: 6,
        marginBottom: 22
      }} data-id="element-97">
            {questions.map((_, i) => <span key={i} style={{
          height: 4,
          flex: 1,
          borderRadius: 999,
          background: i <= qIndex ? MAUVE : CHARCOAL_LINE
        }} data-id="element-98" />)}
          </div>
          <h3 style={{
        fontFamily: serif,
        fontSize: 24,
        lineHeight: 1.2,
        margin: '0 0 22px'
      }} data-id="element-99">
            {questions[qIndex].prompt}
          </h3>
          <button style={choiceBtn} onClick={() => choose(questions[qIndex].a.path)} onMouseEnter={e => e.currentTarget.style.borderColor = MAUVE} onMouseLeave={e => e.currentTarget.style.borderColor = CHARCOAL_LINE} data-id="element-100">
            {questions[qIndex].a.label}
          </button>
          <button style={choiceBtn} onClick={() => choose(questions[qIndex].b.path)} onMouseEnter={e => e.currentTarget.style.borderColor = MAUVE} onMouseLeave={e => e.currentTarget.style.borderColor = CHARCOAL_LINE} data-id="element-101">
            {questions[qIndex].b.label}
          </button>
        </div>}

      {/* RESULT */}
      {step === 'result' && result && <div data-id="element-102">
          <span style={pill} data-id="element-103">Your invisible rule</span>
          <h2 style={{
        fontFamily: serif,
        fontSize: 30,
        lineHeight: 1.1,
        margin: '0 0 6px'
      }} data-id="element-104">
            {result.term}
          </h2>
          <p style={{
        fontSize: 15,
        lineHeight: 1.6,
        color: CHARCOAL_SOFT,
        margin: '0 0 20px'
      }} data-id="element-105">
            {result.definition}
          </p>
          <p style={{
        fontSize: 16,
        lineHeight: 1.6,
        margin: '0 0 24px'
      }} data-id="element-106">
            {result.blurb}
          </p>

          {/* Consequence copy */}
          <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14,
        marginBottom: 26
      }} data-id="element-107">
            <div style={{
          background: '#fff',
          borderRadius: 14,
          padding: 16,
          border: `1px solid ${CHARCOAL_LINE}`
        }} data-id="element-108">
              <div style={{
            fontSize: 11,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: MAUVE,
            fontWeight: 700,
            marginBottom: 6
          }} data-id="element-109">
                Personal
              </div>
              <p style={{
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: 0,
            color: CHARCOAL_SOFT
          }} data-id="element-110">
                {result.personal}
              </p>
            </div>
            <div style={{
          background: '#fff',
          borderRadius: 14,
          padding: 16,
          border: `1px solid ${CHARCOAL_LINE}`
        }} data-id="element-111">
              <div style={{
            fontSize: 11,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: MAUVE,
            fontWeight: 700,
            marginBottom: 6
          }} data-id="element-112">
                Professional
              </div>
              <p style={{
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: 0,
            color: CHARCOAL_SOFT
          }} data-id="element-113">
                {result.professional}
              </p>
            </div>
          </div>

          {/* Famous people cards */}
          <div style={{
        fontSize: 11,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: CHARCOAL_SOFT,
        fontWeight: 700,
        marginBottom: 12
      }} data-id="element-114">
            You share this with
          </div>
          <div style={{
        display: 'flex',
        gap: 10,
        marginBottom: 28,
        flexWrap: 'wrap'
      }} data-id="element-115">
            {result.famous.map(f => <div key={f.name} style={{
          flex: '1 1 120px',
          background: '#fff',
          border: `1px solid ${CHARCOAL_LINE}`,
          borderRadius: 14,
          padding: '14px 12px'
        }} data-id="element-116">
                <div style={{
            fontFamily: serif,
            fontSize: 16,
            marginBottom: 4
          }} data-id="element-117">
                  {f.name}
                </div>
                <div style={{
            fontSize: 12.5,
            color: CHARCOAL_SOFT,
            lineHeight: 1.4
          }} data-id="element-118">
                  {f.note}
                </div>
              </div>)}
          </div>

          {/* LinkedIn share card */}
          <div style={{
        background: CHARCOAL,
        color: CREAM,
        borderRadius: 18,
        padding: 22,
        marginBottom: 24,
        position: 'relative',
        overflow: 'hidden'
      }} data-id="element-119">
            <div style={{
          position: 'absolute',
          top: 14,
          right: 14,
          opacity: 0.5
        }} data-id="element-120">
              <SketchGrid data-id="element-121" />
            </div>
            <div style={{
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: MAUVE,
          fontWeight: 700,
          marginBottom: 10
        }} data-id="element-122">
              Share card
            </div>
            <p style={{
          fontFamily: serif,
          fontSize: 21,
          lineHeight: 1.25,
          margin: '0 0 10px'
        }} data-id="element-123">
              My invisible rule: {result.term}.
            </p>
            <p style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: 'rgba(244,240,232,0.85)',
          margin: '0 0 10px'
        }} data-id="element-124">
              {result.definition}
            </p>
            <p style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: 'rgba(244,240,232,0.6)',
          margin: '0 0 18px'
        }} data-id="element-125">
              In the company of {result.famous.map(f => f.name).join(', ')}. I
              took The Invisible Rule by Underlined by Dania — what's yours?
            </p>
            <div style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap'
        }} data-id="element-126">
              <button style={{
            ...primaryBtn,
            background: MAUVE,
            color: CHARCOAL
          }} onClick={() => alert('In production this exports the card as an image (html-to-image / canvas).')} data-id="element-127">
                Save image
              </button>
              <button onClick={() => postToLinkedIn(result)} style={{
            ...primaryBtn,
            background: 'transparent',
            color: CREAM,
            border: `1.5px solid ${CREAM}`
          }} data-id="element-128">
                Post to LinkedIn
              </button>
            </div>
          </div>

          {/* I disagree */}
          {!disagreeSent ? <form onSubmit={submitDisagree} style={{
        marginBottom: 22
      }} data-id="element-129">
              <label style={{
          fontSize: 13.5,
          fontWeight: 600,
          display: 'block',
          marginBottom: 8
        }} data-id="element-130">
                I disagree with my result
              </label>
              <textarea value={disagree} onChange={e => setDisagree(e.target.value)} rows={3} placeholder="Tell me what it got wrong about you…" style={{
          width: '100%',
          fontFamily: sans,
          fontSize: 14.5,
          color: CHARCOAL,
          background: '#fff',
          border: `1px solid ${CHARCOAL_LINE}`,
          borderRadius: 12,
          padding: 12,
          resize: 'vertical',
          boxSizing: 'border-box',
          marginBottom: 10
        }} data-id="element-131" />
              <button type="submit" style={ghostBtn} data-id="element-132">
                Send feedback
              </button>
            </form> : <div style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        background: '#fff',
        border: `1px solid ${CHARCOAL_LINE}`,
        borderRadius: 12,
        padding: 14,
        marginBottom: 22
      }} data-id="element-133">
              <SketchPathDark data-id="element-134" />
              <span style={{
          fontSize: 14,
          color: CHARCOAL_SOFT
        }} data-id="element-135">
                Noted — thank you. The rulebook is always a draft.
              </span>
            </div>}

          <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap'
      }} data-id="element-136">
            <button style={primaryBtn} onClick={playAgain} data-id="element-137">
              Play again
            </button>
            {onClose && <button style={ghostBtn} onClick={onClose} data-id="element-138">
                Close
              </button>}
          </div>
        </div>}
    </div>;
}
// dark-on-light variant of the path sketch used in the feedback confirmation
function SketchPathDark() {
  return <svg width="40" height="40" viewBox="0 0 56 56" fill="none" aria-hidden="true" style={{
    flexShrink: 0
  }} data-id="element-139">
      <path d="M12 46c2-12 10-14 16-10s12 2 16-8" stroke={MAUVE} strokeWidth="2.2" strokeLinecap="round" strokeDasharray="1 6" data-id="element-140" />
      <path d="M40 18l6 2-2 6" stroke={MAUVE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-141" />
    </svg>;
}